import { action, IObservableValue, observable } from 'mobx';
import { FoundAddress } from '../../LeftPanel/interfaces/foundAddress';
import MAP_TILE, { MapTileParams } from '../../Map/enums/mapTile';

export class MapStore {
    @observable reloadMap: IObservableValue<boolean>;
    @observable stylesLoad: IObservableValue<boolean>;
    @observable map: IObservableValue<any>;
    @observable showDraw: IObservableValue<boolean>;
    @observable styleIds: IObservableValue<string[]>;
    @observable tilesLoad: IObservableValue<boolean>;
    @observable selectedObject: IObservableValue<any>;
    @observable layersLoad: IObservableValue<boolean>;
    @observable hidePreloader: IObservableValue<boolean>;
    @observable mapTile: IObservableValue<MapTileParams>;
    @observable geometryTypes: IObservableValue<string[]>;
    @observable foundAddress: IObservableValue<FoundAddress | null>

    constructor() {
        this.stylesLoad = observable.box<boolean>(false);
        this.reloadMap = observable.box<boolean>(false);
        this.map = observable.box<any>(null);
        this.styleIds = observable.box<string[]>([]);
        this.showDraw = observable.box<boolean>(false);
        this.selectedObject = observable.box<any>(null);
        this.tilesLoad = observable.box<boolean>(false);
        this.layersLoad = observable.box<boolean>(false);
        this.geometryTypes = observable.box<string[]>([]);
        this.hidePreloader = observable.box<boolean>(false);
        this.foundAddress = observable.box<FoundAddress | null>(null);
        this.mapTile = observable.box<MapTileParams>(MAP_TILE.DEFAULT);
    }

    /**
     * Получить список идентификаторов выбранных стилей
     */
    @action('get style ids')
    getStyleIds = (): string[] => {
        return this.styleIds.get();
    };

    /**
     * Обновить список идентификаторов выбранных стилей
     */
    @action('set style ids')
    setStyleIds = (styleIds: string[]): void => {
        this.styleIds.set(styleIds);
    };

    /**
     * Получить тайлы для карты
     */
    @action('get map tile')
    getMapTile = (): MapTileParams => {
        return this.mapTile.get();
    };

    /**
     * Обновить тайлы для карты
     */
    @action('set map tile')
    setMapTile = (mapTile: MapTileParams): void => {
        this.mapTile.set(mapTile);
    };

    /**
     * Получить флаг показа инструментов для рисования
     */
    @action('get show draw')
    getShowDraw = (): boolean => {
        return this.showDraw.get();
    };

    /**
     * Обновить флаг показа инструментов для рисования
     */
    @action('set show draw')
    setShowDraw = (showDraw: boolean): void => {
        this.showDraw.set(showDraw);
    };

    /**
     * Получить флаг загрузки тайлов
     */
    @action('get tiles load')
    getTilesLoad = (): boolean => {
        return this.tilesLoad.get();
    };

    /**
     * Обновить флаг загрузки тайлов
     */
    @action('set tiles load')
    setTilesLoad = (tilesLoad: boolean): void => {
        this.tilesLoad.set(tilesLoad);
        this.setHidePreloader(); // проверяем, нужно ли скрывать прелоудер
    };

    /**
     * Получить флаг загрузки слоёв
     */
    @action('get layers load')
    getLayersLoad = (): boolean => {
        return this.layersLoad.get();
    };

    /**
     * Обновить флаг загрузки слоёв
     */
    @action('set layers load')
    setLayersLoad = (layersLoad: boolean): void => {
        this.layersLoad.set(layersLoad);
        this.setHidePreloader(); // проверяем, нужно ли скрывать прелоудер
    };

    /**
     * Получить флаг скрытия прелоудера
     */
    @action('get hide preloader')
    getHidePreloader = (): boolean => {
        return this.hidePreloader.get();
    };

    /**
     * Обновить флаг скрытия прелоудера
     */
    @action('set hide preloader')
    setHidePreloader = (hidePreloader?: boolean): void => {
        if (typeof hidePreloader !== 'undefined') return this.hidePreloader.set(hidePreloader);
        setTimeout(
            () => this.hidePreloader.set(this.getTilesLoad() && this.getLayersLoad() && this.getStylesLoad()), // когда загружены тайлы, слои, стили, то через пол секунды скрываем прелоудер
            500,
        );
    };

    /**
     * Получить список активных типов геометрий
     */
    @action('get geometry types')
    getGeometryTypes = (): string[] => {
        return this.geometryTypes.get();
    };

    /**
     * Обновить список активных типов геометрий
     */
    @action('set geometry types')
    setGeometryTypes = (geometryTypes: string[]): void => {
        this.geometryTypes.set(geometryTypes);
    };

    /**
     * Получить найденный адрес
     */
    getFoundAddress = (): FoundAddress | null => {
        return this.foundAddress.get();
    }

    /**
     * Сохранить найденный адрес
     */
    @action('set address')
    setFoundAddress = (foundAddress: FoundAddress | null) => {
        this.foundAddress.set(foundAddress);
    }

    /**
     * Получить выбранный на карте объект
     */
    getSelectedObject = (): any => {
        return this.selectedObject.get();
    }

    /**
     * Сохранить выбранный на карте объект
     */
    @action('set selected object')
    setSelectedObject = (selectedObject: any) => {
        this.selectedObject.set(selectedObject);
    }

    @action('set map ngw leaflet')
    setMap = (map: any) => {
        this.map.set(map);
    }

    getMap = (): any => {
        return this.map.get();
    }

    setReloadMap = (reload: boolean) => {
        this.reloadMap.set(reload);
    }

    getReloadMap = (): boolean => {
        return this.reloadMap.get();
    }

    /**
     * Обновить флаг загрузки стилей
     * @param stylesLoad
     */
    setStylesLoad = (stylesLoad: boolean) => {
        this.stylesLoad.set(stylesLoad);
        this.setHidePreloader(); // проверяем, нужно ли скрывать прелоудер
    }

    /**
     * Получить флаг загрузки стилей
     */
    getStylesLoad = (): boolean => {
        return this.stylesLoad.get();
    }
}

const mapStore = new MapStore();

export default mapStore;
